import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { WORDMARK_PATHS, WORDMARK_TRANSFORM, WORDMARK_VIEW_BOX } from "./wordmark-paths";

/**
 * Outline weight in viewBox units, so the border scales with the logo instead of
 * staying a fixed pixel width. The wordmark is 290 units wide and renders
 * anywhere from ~60px (navbar) to 700px (hero); a non-scaling stroke that reads
 * well in the hero would smother the navbar.
 *
 * The R and A are hairline artwork rather than solid shapes, so the stroke eats
 * into them from both sides. 4.2 is about the ceiling: by 4.8 the R's counter
 * and the A's apex start filling in, and by 5.5 the R's bowl has closed up.
 */
const BORDER_WIDTH = 4.2;

type LogoProps = {
  /** Colour of the "JRAT'S" lettering */
  wordmark?: string;
  /** Colour of the "Studio" script */
  studio?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /** Hide from assistive tech (e.g. duplicate copies used purely for visuals) */
  decorative?: boolean;
  /** Draw an outline around the JRAT'S letterforms */
  bordered?: boolean;
  /** Outline colour, when `bordered`. A green one step lighter than the pine
   *  "Studio" ink: dark enough to hold each letter's edge against the mint
   *  fill, light enough not to read as a hard rule around the wordmark. */
  borderColor?: string;
  /**
   * Tag the two ink layers with `data-logo-wordmark` / `data-logo-studio` so a
   * parent can animate them independently. The layers are CSS masks over a
   * coloured box, so an animation drives the box (a clip, a transform) and the
   * mask keeps it inside the letterforms.
   */
  animatable?: boolean;
};

export const Logo = ({
  wordmark = "var(--brand-logo-wordmark)",
  studio = "var(--brand-logo-studio)",
  className,
  style,
  title = "JRAT'S Studio",
  decorative = false,
  bordered = false,
  borderColor = "var(--brand-logo-border)",
  animatable = false,
}: LogoProps) => (
  <span
    role={decorative ? undefined : "img"}
    aria-label={decorative ? undefined : title}
    aria-hidden={decorative || undefined}
    className={cn("relative block select-none", className)}
    style={{ aspectRatio: "290 / 152", ...style }}
  >
    {/* Outline layer. Sits under the wordmark fill, so only the half of the
        stroke that falls outside each letter shows and the letterform keeps its
        exact weight. `paint-order` is not enough on its own here: the fill is a
        separate masked layer above, not part of this SVG. */}
    {bordered && (
      <svg
        aria-hidden
        viewBox={WORDMARK_VIEW_BOX}
        className="pointer-events-none absolute inset-0 z-0 h-full w-full"
      >
        <g transform={WORDMARK_TRANSFORM}>
          {WORDMARK_PATHS.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={borderColor}
              strokeWidth={BORDER_WIDTH}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </g>
      </svg>
    )}
    {/* Wordmark layer, above its own outline but below the script */}
    <span
      aria-hidden
      data-logo-wordmark={animatable ? "" : undefined}
      className="logo-mask logo-mask--wordmark relative z-10"
      style={{ backgroundColor: wordmark }}
    />
    {/* Studio script layer, on top: its "S" and the ascender of the "t" cross
        the R and the A, so the script paints over the wordmark there. */}
    <span
      aria-hidden
      data-logo-studio={animatable ? "" : undefined}
      className="logo-mask logo-mask--studio relative z-20"
      style={{ backgroundColor: studio }}
    />
  </span>
);
